// ---------------------------------------------------------------------------
// Content helpers – used for measuring and splitting rich-text content.
// Supports both Strapi blocks (array) and plain HTML strings.
// ---------------------------------------------------------------------------

/** Count opening <h2> tags in an HTML fragment (case-insensitive). */
export function countH2OpenTags(html: string): number {
  return [...html.matchAll(new RegExp("<h2\\b[^>]*>", "gi"))].length;
}

/**
 * Inserts `<hr>` immediately before each <h2> whose 1-based index in the overall
 * article prose flow is greater than 1. Use `priorH2Count` for headings that
 * appear before this fragment (e.g. the static “Key Takeaways” h2 on news).
 * Pair with page-specific prose wrappers (e.g. `.chart-seo-content hr`, `.cyph-tracker .info-section hr`).
 */
export function injectHrBeforeSubsequentH2(html: string, priorH2Count: number): string {
  let seenInFragment = 0;
  return html.replace(new RegExp("<h2\\b[^>]*>", "gi"), (match) => {
    seenInFragment += 1;
    const globalIndex = priorH2Count + seenInFragment;
    return globalIndex <= 1 ? match : `<hr>${match}`;
  });
}

/** Remove trailing `<hr>` tags and empty `<h2>` blocks often left by the CMS. */
export function stripTrailingHrs(html: string): string {
  let out = html.trimEnd();
  for (;;) {
    const prev = out;
    out = out.replace(/(\s*<hr\b[^>]*\/?>)+\s*$/gi, "");
    out = out.replace(
      /\s*<h2\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/h2>\s*$/gi,
      ""
    );
    if (out === prev) break;
  }
  return out;
}

// Convert relative internal links to absolute paths
export function fixInternalLinks(html: string): string {
  // Convert relative links like href="charts/..." to href="/charts/..."
  return html.replace(/href="(?!\/|http|mailto|#)([^"]*?)"/g, 'href="/$1"');
}

// Strip HTML tags to get plain text length
export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

export function getContentCharCount(content: unknown): number {
  if (!content) return 0;
  if (typeof content === "string") return stripHtml(content).length;
  if (Array.isArray(content)) {
    const countText = (nodes: unknown[]): number =>
      nodes.reduce((acc: number, node) => {
        if (!node || typeof node !== "object") return acc;
        const n = node as { type?: string; text?: string; children?: unknown[] };
        if (n.type === "text") return acc + (n.text?.length ?? 0);
        if (n.children) return acc + countText(n.children);
        return acc;
      }, 0);
    return countText(content);
  }
  return 0;
}

// Block-level closing tags to split HTML at
const BLOCK_CLOSING_TAGS = [
  "</p>",
  "</h1>",
  "</h2>",
  "</h3>",
  "</h4>",
  "</h5>",
  "</h6>",
  "</ul>",
  "</ol>",
  "</blockquote>",
  "</pre>",
  "</figure>",
];

/** True if `splitIndex` sits inside an unclosed <table> (nested tables supported). */
function isInsideOpenHtmlTable(html: string, splitIndex: number): boolean {
  const before = html.slice(0, splitIndex);
  const opens = (before.match(/<table\b/gi) ?? []).length;
  const closes = (before.match(/<\/table>/gi) ?? []).length;
  return opens > closes;
}

/**
 * If the split would break a table, move it to after the closing </table> so the CTA
 * never sits between table markup.
 */
function moveSplitAfterHtmlTables(html: string, splitAt: number): number {
  let pos = splitAt;
  const lower = html.toLowerCase();
  while (isInsideOpenHtmlTable(html, pos)) {
    const idx = lower.indexOf("</table>", pos);
    if (idx === -1) {
      return html.length;
    }
    pos = idx + "</table>".length;
  }
  return pos;
}

export function splitContentAtMiddle(content: unknown): [unknown, unknown] {
  if (Array.isArray(content)) {
    const mid = Math.ceil(content.length / 2);
    return [content.slice(0, mid), content.slice(mid)];
  }
  if (typeof content === "string") {
    const mid = Math.floor(content.length / 2);
    // Find the first block-level closing tag at or after the midpoint
    let bestIndex = -1;
    let bestTagLength = 0;
    for (const tag of BLOCK_CLOSING_TAGS) {
      const idx = content.indexOf(tag, mid);
      if (idx !== -1 && (bestIndex === -1 || idx < bestIndex)) {
        bestIndex = idx;
        bestTagLength = tag.length;
      }
    }
    if (bestIndex !== -1) {
      let splitAt = bestIndex + bestTagLength;
      splitAt = moveSplitAfterHtmlTables(content, splitAt);
      return [content.slice(0, splitAt), content.slice(splitAt)];
    }
    // Fallback: split at nearest space
    const spaceIndex = content.indexOf(" ", mid);
    if (spaceIndex !== -1) {
      let splitAt = spaceIndex;
      splitAt = moveSplitAfterHtmlTables(content, splitAt);
      return [content.slice(0, splitAt), content.slice(splitAt)];
    }
    let splitAt = mid;
    splitAt = moveSplitAfterHtmlTables(content, splitAt);
    return [content.slice(0, splitAt), content.slice(splitAt)];
  }
  return [content, null];
}

export function getYTDLimit() {
  const today = new Date();
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const daysPassed = Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return daysPassed;
}